import { useEffect, useState } from 'react';
import {
  Form, Input, Select, InputNumber, Row, Col, Switch, Typography, Divider, Tooltip
} from 'antd';
import { studioLookups } from '../../itemsApi';
import {
  GLASS_TYPES, GLASS_FAMILIES, GLASS_SURFACE_TYPES, GLASS_SHAPE_TYPES, GLASS_PROCESS_TYPES,
  PROFILE_MATERIALS, PROFILE_USAGES, PROFILE_POSITIONS, PROFILE_FINISH_TYPES, PROFILE_COATING_TYPES,
  HARDWARE_TYPES, HARDWARE_CATEGORIES, HARDWARE_USAGES, HARDWARE_POSITIONS,
  HARDWARE_MATERIALS, HARDWARE_FINISH_TYPES, HARDWARE_COATING_TYPES,
  JOBWORK_TYPES, JOBWORK_CATEGORIES, JOBWORK_PROCESS_TYPES,
  JOBWORK_DIRECTIONS, JOBWORK_MATERIAL_MODES,
  JOBWORK_CHARGE_BASES, JOBWORK_RATE_BASES,
  TECHNICAL_SECTIONS, humaniseEnum
} from '../../studioConstants';

const { Text } = Typography;
const { TextArea } = Input;

// ─────────────────────────────────────────────────────────────────
// Step 4 — Technical Details.
//
// Conditional 1:1 extension panels. Each panel is toggled on/off
// independently; smart defaults are inferred from Step-2 values by
// the parent and passed in via `enabledSections`. The user can
// override at any time.
//
// The form fields here live under top-level keys prefixed with the
// extension name (glassThicknessMm, profileMaterial, etc.) — the
// parent's payload builders strip-and-route each prefix to the matching
// upsert endpoint. That keeps the Form value tree flat and easy to
// reason about.
// ─────────────────────────────────────────────────────────────────

function SectionCard({ keyName, label, helper, enabled, onToggle, children, disabled }) {
  return (
    <div className={`erp-studio-toggle-card ${enabled ? 'is-on' : ''}`}>
      <div className="erp-studio-toggle-header">
        <div>
          <h3>{label}</h3>
          <p>{helper}</p>
        </div>
        <Switch
          disabled={disabled}
          checked={enabled}
          onChange={(v) => onToggle(keyName, v)}
        />
      </div>
      {enabled ? (
        <div className="erp-studio-toggle-body">
          {children}
        </div>
      ) : null}
    </div>
  );
}

function GlassPanel({ disabled, polishTypes }) {
  return (
    <>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Thickness (mm)" name="thicknessMm">
            <InputNumber min={0} step={0.5} precision={3} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Glass type" name="glassType">
            <Select allowClear disabled={disabled}
              options={GLASS_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Glass family" name="glassFamily">
            <Select allowClear disabled={disabled}
              options={GLASS_FAMILIES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Process" name="processType">
            <Select allowClear disabled={disabled}
              options={GLASS_PROCESS_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Colour" name="glassColor">
            <Input disabled={disabled} placeholder="e.g. Clear, Bronze" />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Tint" name="tintColor">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Surface" name="surfaceType">
            <Select allowClear disabled={disabled}
              options={GLASS_SURFACE_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Shape" name="shapeType">
            <Select allowClear disabled={disabled}
              options={GLASS_SHAPE_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Coating" name="coatingType">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Edge work" name="edgeWorkType">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="Polish type" name="polishTypeId">
            <Select allowClear showSearch optionFilterProp="label" disabled={disabled}
              options={polishTypes.map((p) => ({
                value: p.id,
                label: `${p.polishTypeCode || p.polish_type_code} — ${p.polishTypeName || p.polish_type_name}`
              }))} />
          </Form.Item>
        </Col>
      </Row>
      <div className="erp-studio-flags" style={{ marginTop: 4 }}>
        <Form.Item label="Toughenable" name="isToughenable" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Toughened" name="isToughened" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Laminated" name="isLaminated" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="DGU" name="isDgu" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Heat strengthened" name="isHeatStrengthened" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Bend glass" name="isBendGlass" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
      </div>
      <Form.Item label="Glass remarks" name="glassRemarks" rules={[{ max: 2000 }]}>
        <TextArea autoSize={{ minRows: 2, maxRows: 3 }} disabled={disabled} />
      </Form.Item>
    </>
  );
}

function ProfilePanel({ disabled }) {
  return (
    <>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="System" name="profileSystem">
            <Input disabled={disabled} placeholder="e.g. Sliding 2-track" />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Material" name="profileMaterial">
            <Select allowClear disabled={disabled}
              options={PROFILE_MATERIALS.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Series" name="profileSeries">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Code" name="profileCode">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Profile name" name="profileName">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Usage" name="profileUsage">
            <Select allowClear disabled={disabled}
              options={PROFILE_USAGES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Position" name="profilePosition">
            <Select allowClear disabled={disabled}
              options={PROFILE_POSITIONS.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Colour" name="profileColor">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Finish" name="finishType">
            <Select allowClear disabled={disabled}
              options={PROFILE_FINISH_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Coating" name="coatingType">
            <Select allowClear disabled={disabled}
              options={PROFILE_COATING_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Surface finish" name="surfaceFinish">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Brand" name="brand">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Length (mm)" name="lengthMm">
            <InputNumber min={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Section W (mm)" name="sectionWidthMm">
            <InputNumber min={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Section H (mm)" name="sectionHeightMm">
            <InputNumber min={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Wall thickness (mm)" name="wallThicknessMm">
            <InputNumber min={0} step={0.1} precision={3} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Thickness (mm)" name="profileThicknessMm">
            <InputNumber min={0} step={0.1} precision={3} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Weight / metre" name="weightPerMeter">
            <InputNumber min={0} step={0.001} precision={4} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Model no." name="modelNo">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Manufacturer code" name="manufacturerCode">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
      <div className="erp-studio-flags" style={{ marginTop: 4 }}>
        <Form.Item label="Aluminium" name="isAluminium" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="UPVC" name="isUpvc" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Slim profile" name="isSlimProfile" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Frame" name="isFrameProfile" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Shutter" name="isShutterProfile" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Track" name="isTrackProfile" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Channel" name="isChannelProfile" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Beading" name="isBeadingProfile" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Partition" name="isPartitionProfile" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
      </div>
      <Form.Item label="Profile remarks" name="profileRemarks" rules={[{ max: 2000 }]}>
        <TextArea autoSize={{ minRows: 2, maxRows: 3 }} disabled={disabled} />
      </Form.Item>
    </>
  );
}

function HardwarePanel({ disabled }) {
  return (
    <>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Type" name="hardwareType">
            <Select allowClear showSearch optionFilterProp="label" disabled={disabled}
              options={HARDWARE_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Category" name="hardwareCategory">
            <Select allowClear disabled={disabled}
              options={HARDWARE_CATEGORIES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Subcategory" name="hardwareSubcategory">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Usage" name="hardwareUsage">
            <Select allowClear disabled={disabled}
              options={HARDWARE_USAGES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Position" name="hardwarePosition">
            <Select allowClear disabled={disabled}
              options={HARDWARE_POSITIONS.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Code" name="hardwareCode">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="Hardware name" name="hardwareName">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Material" name="material">
            <Select allowClear disabled={disabled}
              options={HARDWARE_MATERIALS.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Finish" name="hwFinishType" tooltip="Maps to finishType on save.">
            <Select allowClear disabled={disabled}
              options={HARDWARE_FINISH_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Coating" name="hwCoatingType" tooltip="Maps to coatingType on save.">
            <Select allowClear disabled={disabled}
              options={HARDWARE_COATING_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Colour" name="color">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Brand" name="hwBrand" tooltip="Maps to brand on save.">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Model" name="hwModelNo">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Mfg code" name="hwManufacturerCode">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Supplier part" name="supplierPartNo">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Size label" name="sizeLabel">
            <Input disabled={disabled} placeholder="e.g. 100mm or M8x20" />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Length (mm)" name="hwLengthMm">
            <InputNumber min={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Width (mm)" name="widthMm">
            <InputNumber min={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Height (mm)" name="heightMm">
            <InputNumber min={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Diameter (mm)" name="diameterMm">
            <InputNumber min={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Thickness (mm)" name="hwThicknessMm">
            <InputNumber min={0} step={0.1} precision={3} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Weight / unit (kg)" name="hwWeightPerUnit">
            <InputNumber min={0} step={0.001} precision={4} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Load capacity (kg)" name="loadCapacityKg">
            <InputNumber min={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={8}>
          <Form.Item label="Compatible system" name="compatibleSystem">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Compatible profile" name="compatibleProfile">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={4}>
          <Form.Item
            label={<Tooltip title="Minimum glass thickness this hardware accepts.">Compat min (mm)</Tooltip>}
            name="compatibleGlassThicknessMinMm"
          >
            <InputNumber min={0} step={0.5} precision={3} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={4}>
          <Form.Item label="Compat max (mm)" name="compatibleGlassThicknessMaxMm">
            <InputNumber min={0} step={0.5} precision={3} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <div className="erp-studio-flags" style={{ marginTop: 4 }}>
        <Form.Item label="Lockable" name="isLockable" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Movable" name="isMovable" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Visible" name="isVisible" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Consumable" name="hwIsConsumable" valuePropName="checked" labelCol={{ span: 14 }}
          tooltip="Maps to isConsumable on the hardware spec (separate from core flag).">
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Fastener" name="isFastener" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Glass hardware" name="isGlassHardware" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Profile hardware" name="isProfileHardware" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Shower hardware" name="isShowerHardware" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Door hardware" name="isDoorHardware" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Window hardware" name="isWindowHardware" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Facade hardware" name="isFacadeHardware" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Partition hardware" name="isPartitionHardware" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
      </div>
      <Form.Item label="Hardware remarks" name="hardwareRemarks" rules={[{ max: 2000 }]}>
        <TextArea autoSize={{ minRows: 2, maxRows: 3 }} disabled={disabled} />
      </Form.Item>
    </>
  );
}

function JobworkPanel({ disabled }) {
  return (
    <>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Type" name="jobworkType">
            <Select allowClear disabled={disabled}
              options={JOBWORK_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Category" name="jobworkCategory">
            <Select allowClear disabled={disabled}
              options={JOBWORK_CATEGORIES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Subcategory" name="jobworkSubcategory">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Process" name="jobworkProcessType">
            <Select allowClear showSearch optionFilterProp="label" disabled={disabled}
              options={JOBWORK_PROCESS_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Direction" name="jobworkDirection">
            <Select allowClear disabled={disabled}
              options={JOBWORK_DIRECTIONS.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Material mode" name="jobworkMaterialMode">
            <Select allowClear disabled={disabled}
              options={JOBWORK_MATERIAL_MODES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Code" name="jobworkCode">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Name" name="jobworkName">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
      <Divider className="erp-studio-divider" />
      <Text strong style={{ fontSize: 13 }}>Charging</Text>
      <Row gutter={[12, 0]} style={{ marginTop: 8 }}>
        <Col xs={24} md={6}>
          <Form.Item label="Charge basis" name="chargeBasis">
            <Select allowClear disabled={disabled}
              options={JOBWORK_CHARGE_BASES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Rate basis" name="rateBasis">
            <Select allowClear disabled={disabled}
              options={JOBWORK_RATE_BASES.map((s) => ({ value: s, label: humaniseEnum(s) }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Minimum charge" name="minimumCharge">
            <InputNumber min={0} step={1} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Minimum qty" name="minimumQty">
            <InputNumber min={0} step={1} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Wastage %" name="wastagePercent">
            <InputNumber min={0} max={100} step={0.1} precision={2} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Process loss %" name="processLossPercent">
            <InputNumber min={0} max={100} step={0.1} precision={2} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Lead time (days)" name="standardLeadTimeDays">
            <InputNumber min={0} step={1} precision={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Process time (mins)" name="standardProcessTimeMinutes">
            <InputNumber min={0} step={5} precision={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Divider className="erp-studio-divider" />
      <Text strong style={{ fontSize: 13 }}>Processing constraints</Text>
      <Row gutter={[12, 0]} style={{ marginTop: 8 }}>
        <Col xs={24} md={6}>
          <Form.Item label="Min thickness (mm)" name="minThicknessMm">
            <InputNumber min={0} step={0.5} precision={3} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Max thickness (mm)" name="maxThicknessMm">
            <InputNumber min={0} step={0.5} precision={3} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Min area (sqft)" name="minAreaSqft">
            <InputNumber min={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Max area (sqft)" name="maxAreaSqft">
            <InputNumber min={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Min area (sqm)" name="minAreaSqm">
            <InputNumber min={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Max area (sqm)" name="maxAreaSqm">
            <InputNumber min={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Min length (mm)" name="minLengthMm">
            <InputNumber min={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Max length (mm)" name="maxLengthMm">
            <InputNumber min={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item label="Min width (mm)" name="minWidthMm">
            <InputNumber min={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Max width (mm)" name="maxWidthMm">
            <InputNumber min={0} disabled={disabled} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Divider className="erp-studio-divider" />
      <Text strong style={{ fontSize: 13 }}>Flags & compatibility</Text>
      <div className="erp-studio-flags" style={{ marginTop: 4 }}>
        <Form.Item label="Requires machine" name="requiresMachine" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Requires QC" name="requiresQc" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Requires outsource" name="requiresOutsource" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Internal process" name="isInternalProcess" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Customer-chargeable" name="isCustomerChargeable" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Supplier-billable" name="isSupplierBillable" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Installation service" name="isInstallationService" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Transport service" name="isTransportService" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
        <Form.Item label="Labour service" name="isLabourService" valuePropName="checked" labelCol={{ span: 14 }}>
          <Switch disabled={disabled} />
        </Form.Item>
      </div>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={8}>
          <Form.Item label="Compatible glass type" name="compatibleGlassType">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Compatible material type" name="compatibleMaterialType">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Compatible process group" name="compatibleProcessGroup">
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item label="Jobwork remarks" name="jobworkRemarks" rules={[{ max: 2000 }]}>
        <TextArea autoSize={{ minRows: 2, maxRows: 3 }} disabled={disabled} />
      </Form.Item>
    </>
  );
}

export default function TechnicalDetailsStep({
  disabled, enabledSections, onToggleSection
}) {
  const [polishTypes, setPolishTypes] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await studioLookups.polishTypes({ q: '', isActive: true, limit: 200 });
        if (!cancelled) setPolishTypes(Array.isArray(rows) ? rows : []);
      } catch (_e) { /* swallow */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div>
      <h2 className="erp-studio-section-title">Technical details</h2>
      <p className="erp-studio-section-help">
        Enable only the technical sections that match this item. Hybrid items (e.g. a coated
        toughened glass that is also installed) can have multiple sections on.
      </p>

      {TECHNICAL_SECTIONS.map((s) => (
        <SectionCard
          key={s.key}
          keyName={s.key}
          label={s.label}
          helper={s.helperText}
          enabled={Boolean(enabledSections && enabledSections[s.key])}
          onToggle={onToggleSection}
          disabled={disabled}
        >
          {s.key === 'glass'    ? <GlassPanel    disabled={disabled} polishTypes={polishTypes} /> : null}
          {s.key === 'profile'  ? <ProfilePanel  disabled={disabled} /> : null}
          {s.key === 'hardware' ? <HardwarePanel disabled={disabled} /> : null}
          {s.key === 'jobwork'  ? <JobworkPanel  disabled={disabled} /> : null}
        </SectionCard>
      ))}
    </div>
  );
}
